import { canReconcileTerminals } from '../../../helpers/terminal-reconciliation.helpers';
import { canCommunityManagerAccess } from '../../../helpers/community-manager';
import { useEffect, useRef, useState } from 'react';
import { NavLink } from 'react-router-dom';
import { collapse, logo, menu, power } from '../../../assets/svg';
import { navLinks } from '../../../constants';
import { useAppDispatch, useAppSelector } from '../../../hooks';
import { authSelector, logout } from '../../../store/slices';
import { LogoutLoader } from '../shared';

// Define types for navLinks
interface SubMenuItem {
  name: string;
  path: string;
  icon: string;
  restrictions: string[];
  exemptions?: string[];
}

interface NavLinkItem {
  name: string;
  path?: string;
  icon: string;
  restrictions: string[];
  allowedRoles?: string[];
  exemptions?: string[];
  isSubmenu?: boolean;
  subMenuItems?: SubMenuItem[];
}

export const Sidebar = () => {
  const dispatch = useAppDispatch();
  const { loading, user } = useAppSelector(authSelector);

  const Links = useRef<HTMLInputElement>(null);

  const [navOpen, setNavOpen] = useState<boolean>(false);
  const [navCollapsed, setNavCollapsed] = useState<boolean>(false);
  const [linksScrollable, setLinksScrollable] = useState(false);
  const [openSubmenuIndex, setOpenSubmenuIndex] = useState<number | null>(null);

  const logoutUser = () => {
    dispatch(logout());
  };

  const toggleSidebar = () => {
    setNavOpen((prev) => !prev);
  };

  const handleCollapse = () => {
    setNavCollapsed((prev) => !prev);
    setOpenSubmenuIndex(null);
  };

  const toggleSubmenu = (index: number) => {
    setOpenSubmenuIndex((prevIndex) => (prevIndex === index ? null : index));
  };

  useEffect(() => {
    if (Links && Links.current) {
      setLinksScrollable(
        Links.current?.scrollHeight > Links.current?.clientHeight
      );
    }
  }, [Links, openSubmenuIndex]);

  // Helper function to check if a nav item should be shown based on user roles
  const shouldShowNavItem = (item: NavLinkItem | SubMenuItem) => {
    if (
      item.path === '/dashboard/terminal-reconciliation' &&
      !canReconcileTerminals(user?.admin)
    )
      return false;
    if (user?.admin?.roles.includes('COMMUNITY_MANAGER')) {
      return !!item.path && canCommunityManagerAccess(item.path);
    }

    if ('allowedRoles' in item && item.allowedRoles) {
      return Boolean(
        user?.admin?.isSuper ||
          user?.admin?.roles.some((role) => item.allowedRoles?.includes(role))
      );
    }

    return (
      !user?.admin?.roles.some((role) => item.restrictions.includes(role)) ||
      (item.exemptions &&
        item.exemptions.includes('kitchenPartners') &&
        user?.admin?.kitchens)
    );
  };

  return (
    <div
      className={`relative ease-in-out transition-all duration-300 z-40 flex-shrink-0 ${
        navCollapsed ? 'lg:w-[7rem] overflow-hidden' : 'lg:w-[20rem]'
      }`}
    >
      <div
        className={`bg-black absolute bg-opacity-10 min-h-screen h-full w-screen z-40 inset-0 ${
          navOpen ? 'block lg:hidden' : 'hidden'
        }
    `}
        onClick={toggleSidebar}
      ></div>
      <div
        className={`absolute lg:static left-0 top-0 transform ease-in-out z-50 transition-all duration-500 h-full bg-neutral-900 py-6 px-4 flex flex-col justify-between flex-shrink-0 rounded-r-xl lg:rounded-none ${
          navOpen ? 'translate-x-0' : '-translate-x-[19.7rem] lg:translate-x-0'
        } ${navCollapsed ? 'lg:w-full w-[20rem]' : 'w-[20rem]'}`}
      >
        <button
          className="h-14 flex flex-col justify-center items-end space-y-1 rounded-xl pr-2 w-12 absolute top-10 z-50 -right-5 bg-neutral-900 lg:hidden"
          onClick={toggleSidebar}
        >
          <img src={menu} className="-mr-2.5 h-7" alt="menu" />
        </button>
        <div className="h-[80%] flex flex-col">
          <img src={logo} alt="logo" className="mx-auto mt-4" />
          <div
            className="flex flex-col mt-10 h-[85%] overflow-y-auto hide-scrollbar"
            ref={Links}
          >
            {navLinks.map(
              (navItem, index) =>
                shouldShowNavItem(navItem) && (
                  <div key={navItem.name} className="mt-3">
                    {navItem.isSubmenu ? (
                      <div>
                        <button
                          onClick={() => toggleSubmenu(index)}
                          className={`py-2 px-2.5 text-white flex items-center justify-between w-full capitalize rounded text-sm ${
                            openSubmenuIndex === index ? 'bg-neutral-800' : ''
                          } ${navCollapsed ? 'lg:flex-col lg:gap-1' : ''}`}
                        >
                          <div className="flex items-center">
                            <img
                              src={navItem.icon}
                              alt="icon"
                              className="w-4"
                            />
                            <p
                              className={`semibold transition-all truncate delay-300 ml-3 ${
                                navCollapsed
                                  ? 'overflow-hidden lg:w-0 lg:opacity-0 lg:ml-0'
                                  : 'block'
                              }`}
                            >
                              {navItem.name}
                            </p>
                          </div>
                          <div
                            className={`transition-transform duration-300 ${
                              openSubmenuIndex === index ? 'rotate-180' : ''
                            }`}
                          >
                            <svg
                              width="10"
                              height="6"
                              viewBox="0 0 10 6"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                d="M1 1L5 5L9 1"
                                stroke="white"
                                strokeWidth="1.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          </div>
                        </button>
                        <div
                          className={`transition-all duration-300 overflow-hidden ${
                            openSubmenuIndex === index ? 'max-h-96' : 'max-h-0'
                          }`}
                        >
                          {navItem.subMenuItems?.map(
                            (subItem) =>
                              shouldShowNavItem(subItem) && (
                                <NavLink
                                  to={subItem.path}
                                  key={subItem.name}
                                  className={({ isActive }) =>
                                    `py-2 px-2.5 text-white flex items-center capitalize rounded text-sm mt-1 ${
                                      isActive ? 'bg-neutral-700' : ''
                                    } ${
                                      navCollapsed ? 'lg:justify-center' : ''
                                    }`
                                  }
                                  onClick={toggleSidebar}
                                >
                                  <div
                                    className={`w-4 flex justify-center ${
                                      navCollapsed ? 'lg:ml-0' : 'ml-4'
                                    }`}
                                  >
                                    <img
                                      src={subItem.icon}
                                      alt="icon"
                                      className="w-3"
                                    />
                                  </div>
                                  <p
                                    className={`semibold transition-all truncate delay-300 ml-3 ${
                                      navCollapsed
                                        ? 'overflow-hidden lg:w-0 lg:opacity-0 lg:ml-0'
                                        : 'block'
                                    }`}
                                  >
                                    {subItem.name}
                                  </p>
                                </NavLink>
                              )
                          )}
                        </div>
                      </div>
                    ) : (
                      <NavLink
                        to={navItem.path || '#'}
                        className={({ isActive }) =>
                          `py-2 px-2.5 text-white flex items-center capitalize rounded text-sm ${
                            isActive ? 'bg-neutral-800' : ''
                          } ${navCollapsed ? 'lg:justify-center' : ''}`
                        }
                        onClick={toggleSidebar}
                      >
                        <img src={navItem.icon} alt="icon" className="w-4" />
                        <p
                          className={`semibold transition-all truncate delay-300 ml-3 ${
                            navCollapsed
                              ? 'overflow-hidden lg:w-0 lg:opacity-0 lg:ml-0'
                              : 'block'
                          }`}
                        >
                          {navItem.name}
                        </p>
                      </NavLink>
                    )}
                  </div>
                )
            )}
          </div>
          {linksScrollable && (
            <p className="animate-bounce text-xs text-center mt-2 text-neutral-400">
              scroll
            </p>
          )}
        </div>
        <div className="space-y-5 flex flex-col">
          <button
            onClick={handleCollapse}
            className={`text-white lg:flex items-center focus:outline-none hidden  ${
              navCollapsed ? 'justify-center' : ''
            }`}
          >
            <img
              src={collapse}
              className={`"h-6 w-6 mr-3 transform ${
                navCollapsed ? 'rotate-180' : ''
              }`}
              alt="collapse"
            />
            {!navCollapsed && (
              <span className="truncate">Collapse Sidebar</span>
            )}
          </button>
          <button
            onClick={logoutUser}
            className={`flex items-center text-white focus:outline-none ${
              navCollapsed ? 'lg:justify-center' : ''
            }`}
          >
            {loading ? (
              <div className="h-6 w-6 mr-3 flex items-center">
                <LogoutLoader />
              </div>
            ) : (
              <img className="h-6 w-6 mr-3" src={power} alt="logout" />
            )}
            <span className={`${!navCollapsed ? '' : 'lg:hidden'}`}>
              Logout
            </span>
          </button>
        </div>
      </div>
    </div>
  );
};
